import React, { useEffect, useMemo, useState, useCallback, memo, useRef } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

// Memoized components for better performance
const HourItem = memo(({ item, enrollmentId, index }) => {
  // Check if this is a pending FE event
  const isPendingFE = item.source === 'Event' && item.type === 'Field Experience' && !item.title.includes('(Verified)');
  
  // Clean up the title - remove (Verified) text and add Pending status
  let displayTitle = item.title || '';
  if (item.source === 'Event' && item.type === 'Field Experience') {
    if (displayTitle.includes('(Verified)')) {
      displayTitle = displayTitle.replace(' (Verified)', '');
    } else {
      displayTitle = displayTitle.replace(' (Pending)', '');
    }
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      borderBottom: '1px dashed #e5e7eb', 
      paddingBottom: 6,
      backgroundColor: isPendingFE ? '#fefce8' : 'transparent', // Light yellow for pending FE
      padding: isPendingFE ? '8px' : '0 0 6px 0',
      borderRadius: isPendingFE ? '4px' : '0',
      marginBottom: isPendingFE ? '4px' : '0'
    }}>
      <div style={{ fontSize: 12, color: '#374151' }}>
        <strong>{item.source}</strong> • {item.type} • {new Date(item.date || 0).toLocaleString()}
        {displayTitle ? ` • ${displayTitle}` : ''}
        {isPendingFE && (
          <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: '4px' }}>
            • Pending
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#111827', fontWeight: 700 }}>{item.hours}h</div>
    </div>
  );
});

const EnrollmentDetails = memo(({ enrollment, stats }) => (
  <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10 }}>
    <div style={{ padding: 10, backgroundColor: '#f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {/* <div style={{ fontWeight: 600, color: '#374151' }}>Enrollment: {enrollment.enrollment_id}</div> */}
      <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
        {HOUR_TYPES.map(t => (
          <div key={`${enrollment.enrollment_id}-${t}`}>
            <strong>{(stats?.totals?.[t] || 0)}h</strong> {t}
          </div>
        ))}
      </div>
    </div>
    <div style={{ padding: 10 }}>
      {!stats || stats.items.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>No hour items recorded.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {stats.items.map((item, idx) => (
            <HourItem key={`${enrollment.enrollment_id}-item-${idx}`} item={item} enrollmentId={enrollment.enrollment_id} index={idx} />
          ))}
        </div>
      )}
    </div>
  </div>
));

const StudentDetailsPanel = memo(({ isOpen, onClose, student, enrollments, hoursByEnrollment, loadingEnrollments, position, onPositionChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep panel within viewport bounds
    const maxX = window.innerWidth - 400; // panel width
    const maxY = window.innerHeight - 200; // minimum panel height
    
    onPositionChange({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse events when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (!isOpen || !student) return null;

  return (
    <div style={{
      position: 'fixed',
      top: `${position.y}px`,
      left: `${position.x}px`,
      width: '50vw',
      maxHeight: 'calc(100vh - 40px)',
      backgroundColor: 'white',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      borderRadius: '16px',
      border: '1px solid #e5e7eb',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      cursor: isDragging ? 'grabbing' : 'default'
    }}>
      {/* Header - Draggable */}
      <div 
        onMouseDown={handleMouseDown}
        style={{ 
          padding: '24px', 
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          flexShrink: 0,
          cursor: 'grab',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: '#111827', fontSize: '20px', fontWeight: 600 }}>
              {student.first_name} {student.last_name}
            </h2>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px', fontWeight: 400 }}>
              {student.primary_email}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {enrollments.length === 0 ? (
          <div style={{ 
            fontSize: 14, 
            color: '#6b7280', 
            fontStyle: 'italic', 
            textAlign: 'center', 
            padding: '40px 20px',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #d1d5db'
          }}>
            No enrollments for selected term.
          </div>
        ) : enrollments.some(e => loadingEnrollments.has(e.enrollment_id)) ? (
          <div style={{ 
            fontSize: 14, 
            color: '#6b7280', 
            fontStyle: 'italic', 
            textAlign: 'center', 
            padding: '40px 20px',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #d1d5db'
          }}>
            Loading enrollment details...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {enrollments.map(e => {
              const stats = hoursByEnrollment[e.enrollment_id];
              return (
                <EnrollmentDetails key={e.enrollment_id} enrollment={e} stats={stats} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

// Hour types used across the app
const HOUR_TYPES = ['Field Experience', 'Lab', 'Concentration', 'Capstone', 'Theory'];

// Sentinel for enrollments with null/empty association_id
const UNKNOWN_ASSOCIATION_ID = '__unknown__';

// Aggregate utilities
const createEmptyHours = () => ({
  'Field Experience': 0,
  'Lab': 0,
  'Concentration': 0,
  'Capstone': 0,
  'Theory': 0,
});

/** Parse `tors` on an enrollment (same shape as community /api/user-management/community). */
function parseEnrollmentTors(en) {
  if (!en) return [];
  if (Array.isArray(en.tors)) return en.tors.filter((t) => t && t.account_id);
  if (typeof en.tors === 'string') {
    try {
      const parsed = JSON.parse(en.tors || '[]');
      return Array.isArray(parsed) ? parsed.filter((t) => t && t.account_id) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Primary emails for student accounts where `myAccountId` is an active TEACHER_OF_RECORD on any enrollment. */
function collectTorStudentEmails(myAccountId, accounts) {
  if (!myAccountId || !accounts?.length) return [];
  const emails = new Set();
  const idStr = String(myAccountId);
  for (const acc of accounts) {
    const cards = acc.accesscards || [];
    for (const c of cards) {
      if (c.role !== 'Student') continue;
      for (const en of c.enrollments || []) {
        const tors = parseEnrollmentTors(en);
        if (tors.some((t) => String(t.account_id) === idStr)) {
          const em = (acc.primary_email || '').trim();
          if (em) emails.add(em);
          break;
        }
      }
    }
  }
  return Array.from(emails).sort((a, b) => a.localeCompare(b));
}

function StudentHoursOverview({ userData }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [schools, setSchools] = useState([]);
  const [studentPeriods, setStudentPeriods] = useState([]);
  const [studentAssociations, setStudentAssociations] = useState([]);
  const [accounts, setAccounts] = useState([]); // all accounts from API

  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('All Terms');
  const [selectedAssociations, setSelectedAssociations] = useState(() => new Set());
  const [associationFilterOn, setAssociationFilterOn] = useState(true);
  const [showAssociationPopup, setShowAssociationPopup] = useState(false);
  const hasInitializedAssociations = useRef(false);
  const torLoadPromptOfferedRef = useRef(false);
  const [showTorLoadPrompt, setShowTorLoadPrompt] = useState(false);
  const [pendingTorStudentEmails, setPendingTorStudentEmails] = useState([]);

  const [selectedStudent, setSelectedStudent] = useState(null); // Selected student for panel
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 20 }); // Panel position
  const [hoursByEnrollment, setHoursByEnrollment] = useState({}); // enrollmentId -> { totals, items }
  const [loadingEnrollments, setLoadingEnrollments] = useState(new Set()); // Set of enrollment IDs currently loading
  const [sortBy, setSortBy] = useState('total'); // 'name' | 'total' | hour type
  const [bulkQuery, setBulkQuery] = useState(''); // multiple names/emails, one per line
  const [debouncedQuery, setDebouncedQuery] = useState(''); // debounced version of bulkQuery
  const [countPendingAsVerified, setCountPendingAsVerified] = useState(true); // count pending FE events as verified
  const [includeWithdrawnStudents, setIncludeWithdrawnStudents] = useState(true); // when false, filter out students whose relevant accesscard has inactive === true
  const [currentPage, setCurrentPage] = useState(1); // current page for pagination
  const [showDownloadPopup, setShowDownloadPopup] = useState(false); // show download popup
  const [dateRangeStart, setDateRangeStart] = useState(''); // date range start filter
  const [dateRangeEnd, setDateRangeEnd] = useState(''); // date range end filter
  const [minTotalHours, setMinTotalHours] = useState(0); // minimum total hours threshold
  const [minHoursByType, setMinHoursByType] = useState({
    'Field Experience': 0,
    'Lab': 0,
    'Concentration': 0,
    'Capstone': 0,
    'Theory': 0
  }); // minimum hours threshold by type
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false); // analytics section expanded state
  const [thresholdAnalyticsExpanded, setThresholdAnalyticsExpanded] = useState(false); // threshold analytics section expanded state

  // Debounce the bulk query to prevent excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(bulkQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [bulkQuery]);

  // Load initial data similar to Community.js
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/user-management/community', {
          headers: { Authorization: `Bearer ${token}` }
        });

        setSchools(response.data.schools || []);
        setStudentPeriods(response.data.studentPeriods || []);
        setStudentAssociations(response.data.studentAssociations || []);
        setAccounts(response.data.accounts || []);

        // Default and lock selected school to Scale Leadership Academy by known school_id
        const targetSchool = (response.data.schools || []).find(s => s.school_id === '2c563b85-6afe-46f2-8918-5b15152d6991');
        if (targetSchool) {
          setSelectedSchool(targetSchool);
        } else if (!selectedSchool) {
          // Fallback to first school if SLA not found
          setSelectedSchool((response.data.schools || [])[0] || null);
        }

        // Default to most recent period if available, but allow user to change
        if (response.data.studentPeriods && response.data.studentPeriods.length > 0 && selectedPeriod === 'All Terms') {
          const sorted = [...response.data.studentPeriods].sort((a, b) => b.name.localeCompare(a.name));
          setSelectedPeriod(sorted[0].id);
        }
      } catch (err) {
        setError('Failed to load data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize selectedAssociations: all except 'test', 'FA04', 'Unknown association' (by title)
  useEffect(() => {
    if (studentAssociations.length > 0 && !hasInitializedAssociations.current) {
      hasInitializedAssociations.current = true;
      const defaultIds = studentAssociations
        .filter(a => a.title !== 'test' && a.title !== 'FA04' && a.title?.toLowerCase() !== 'unknown association')
        .map(a => a.id);
      setSelectedAssociations(new Set(defaultIds));
    }
  }, [studentAssociations]);

  // Teachers of record: ask once whether to filter to assigned students (Yes = TOR shortcuts; No = default filters).
  useEffect(() => {
    if (loading || !userData?.account_id || !accounts.length || torLoadPromptOfferedRef.current) return;
    torLoadPromptOfferedRef.current = true;
    const emails = collectTorStudentEmails(userData.account_id, accounts);
    if (emails.length === 0) return;
    setPendingTorStudentEmails(emails);
    setShowTorLoadPrompt(true);
  }, [loading, accounts, userData?.account_id]);

  const acceptTorAssignedStudents = useCallback(() => {
    setShowTorLoadPrompt(false);
    setAssociationFilterOn(false);
    setBulkQuery(pendingTorStudentEmails.join('\n'));
  }, [pendingTorStudentEmails]);

  const declineTorAssignedStudents = useCallback(() => {
    setShowTorLoadPrompt(false);
    setPendingTorStudentEmails([]);
  }, []);

  // Helper: does enrollment match the association filter?
  const enrollmentMatchesAssociation = useCallback((enrollment) => {
    if (!associationFilterOn || selectedAssociations.size === 0) return true;
    return selectedAssociations.has(enrollment.association_id) ||
      (selectedAssociations.has(UNKNOWN_ASSOCIATION_ID) && (enrollment.association_id == null || enrollment.association_id === ''));
  }, [associationFilterOn, selectedAssociations]);

  // Filter to student accounts at selected school, period, and association
  const studentAccounts = useMemo(() => {
    let base = accounts.filter(acc => acc.accesscards?.some(c => c.role === 'Student'));
    if (selectedSchool) {
      base = base.filter(acc => acc.accesscards?.some(c => c.role === 'Student' && c.school_id === selectedSchool.school_id));
    }
    // Require at least one enrollment matching BOTH period AND association (so we don't show students with "No enrollments for selected term")
    const assocFilterActive = associationFilterOn && selectedAssociations.size > 0;
    if (selectedPeriod !== 'All Terms' || assocFilterActive) {
      base = base.filter(acc =>
        acc.accesscards?.some(c =>
          c.role === 'Student' &&
          c.enrollments &&
          c.enrollments.some(e => {
            const periodMatch = selectedPeriod === 'All Terms' || e.period_id === selectedPeriod;
            const assocMatch = enrollmentMatchesAssociation(e);
            return periodMatch && assocMatch;
          })
        )
      );
    }
    // When includeWithdrawnStudents is false: filter out students whose relevant access card (matching period/association) is inactive
    if (!includeWithdrawnStudents) {
      base = base.filter(acc =>
        acc.accesscards?.some(c =>
          c.role === 'Student' &&
          (c.inactive === false || c.inactive === null || c.inactive === undefined) &&
          (c.enrollments || []).some(e => {
            const periodMatch = selectedPeriod === 'All Terms' || e.period_id === selectedPeriod;
            const assocMatch = enrollmentMatchesAssociation(e);
            return periodMatch && assocMatch;
          })
        )
      );
    }
    // Sort by school then name for stable grouping
    return base.sort((a, b) => {
      const aSchool = (a.accesscards.find(c => c.role === 'Student')?.school_name || '').toLowerCase();
      const bSchool = (b.accesscards.find(c => c.role === 'Student')?.school_name || '').toLowerCase();
      if (aSchool !== bSchool) return aSchool.localeCompare(bSchool);
      const an = `${a.first_name} ${a.last_name}`.toLowerCase();
      const bn = `${b.first_name} ${b.last_name}`.toLowerCase();
      return an.localeCompare(bn);
    });
  }, [accounts, selectedSchool, selectedPeriod, selectedAssociations, associationFilterOn, includeWithdrawnStudents, enrollmentMatchesAssociation]);

  // Set initial panel position on mount
  useEffect(() => {
    setPanelPosition({ x: window.innerWidth - 650, y: 20 });
  }, []);

  const selectStudent = useCallback((student) => {
    setSelectedStudent(student);
  }, []);

  const closeStudentPanel = useCallback(() => {
    setSelectedStudent(null);
  }, []);

  // Fetch hours for enrollments lazily
  const fetchEnrollmentHoursBulk = useCallback(async (enrollmentIds) => {
    if (!Array.isArray(enrollmentIds) || enrollmentIds.length === 0) return;
    // Determine which IDs we still need
    const pending = enrollmentIds.filter(id => !hoursByEnrollment[id] && !loadingEnrollments.has(id));
    if (pending.length === 0) return;
    
    // Mark as loading
    setLoadingEnrollments(prev => new Set([...prev, ...pending]));
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/hours/bulk', { enrollments: pending }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const regs = res.data.registrations || [];
      const projs = res.data.projectSubmissions || [];
      const waivers = res.data.hourwaivers || [];
      const compSubs = res.data.componentSubmissions || [];

      // Build per-enrollment aggregates
      const build = {};
      const ensure = (id) => {
        if (!build[id]) build[id] = { totals: createEmptyHours(), items: [] };
        return build[id];
      };

      regs.forEach(r => {
        const bucket = ensure(r.enrollment);
        // Include FE events - when countPendingAsVerified is true, treat pending as verified
        const shouldInclude = r.attended === true && typeof r.hours === 'number';
        
        if (shouldInclude) {
          bucket.totals['Field Experience'] += r.hours;
          // When countPendingAsVerified is true, mark pending events as Verified
          const statusText = countPendingAsVerified || r.status === 'Verified' ? 'Verified' : (r.status || 'Pending');
          bucket.items.push({ 
            source: 'Event', 
            type: 'Field Experience', 
            hours: r.hours, 
            date: r.event_date, 
            title: `${r.event_title || 'Field Experience Event'} (${statusText})` 
          });
        }
      });

      projs.forEach(p => {
        const bucket = ensure(p.enrollment);
        if (p.status === 'complete' && typeof p.hoursreceived === 'number') {
          const t = p.type || 'Field Experience';
          if (bucket.totals[t] !== undefined) bucket.totals[t] += p.hoursreceived;
          bucket.items.push({ source: 'Project', type: t, hours: p.hoursreceived, date: p.createddate, title: p.projecttitle || 'Project' });
        }
      });

      waivers.forEach(w => {
        const bucket = ensure(w.enrollment);
        if (w.status === 'Verified' && typeof w.hours === 'number') {
          const t = w.type || 'Field Experience';
          if (bucket.totals[t] !== undefined) bucket.totals[t] += w.hours;
          bucket.items.push({ source: 'Waiver', type: t, hours: w.hours, date: w.date, title: 'External Activity' });
        }
      });

      compSubs.forEach(cs => {
        const bucket = ensure(cs.enrollment);
        if (cs.status === 'completed' && cs.hours_earned) {
          try {
            const hours = typeof cs.hours_earned === 'string' ? JSON.parse(cs.hours_earned) : cs.hours_earned;
            if (Array.isArray(hours)) {
              hours.forEach(h => {
                const t = h.type;
                const val = Number(h.hours) || 0;
                if (t && bucket.totals[t] !== undefined) {
                  bucket.totals[t] += val;
                  bucket.items.push({ source: 'Coursework', type: t, hours: val, date: cs.createddate, title: cs.component_title || 'Component' });
                }
              });
            }
          } catch {}
        }
      });

      // Ensure empty buckets for enrollments with zero data
      pending.forEach(id => {
        if (!build[id]) build[id] = { totals: createEmptyHours(), items: [] };
      });

      // Sort each bucket and commit to state
      Object.keys(build).forEach(id => {
        build[id].items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      });

      setHoursByEnrollment(prev => ({ ...prev, ...build }));
    } catch (err) {
      console.error('Failed to fetch bulk hours', err);
    } finally {
      // Remove from loading set
      setLoadingEnrollments(prev => {
        const newSet = new Set(prev);
        pending.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [hoursByEnrollment, loadingEnrollments, countPendingAsVerified]);

  // Prefetch ALL enrollment hours for current filter on page load and when filters change
  const enrollmentIdsMemo = useMemo(() => {
    const ids = [];
    for (const acc of studentAccounts) {
      const cards = acc.accesscards || [];
      for (const c of cards) {
        if (c.role !== 'Student') continue;
        const ens = c.enrollments || [];
        for (const e of ens) {
          if (selectedPeriod === 'All Terms' || e.period_id === selectedPeriod) ids.push(e.enrollment_id);
        }
      }
    }
    return ids;
  }, [studentAccounts, selectedPeriod]);

  useEffect(() => {
    fetchEnrollmentHoursBulk(enrollmentIdsMemo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentIdsMemo, countPendingAsVerified]);

  // Helper function to check if item date is within date range
  const isItemInDateRange = useCallback((itemDate) => {
    if (!dateRangeStart && !dateRangeEnd) return true; // No date filter
    if (!itemDate) return false;
    
    const itemDateObj = new Date(itemDate);
    const startDate = dateRangeStart ? new Date(dateRangeStart) : null;
    const endDate = dateRangeEnd ? new Date(dateRangeEnd + 'T23:59:59') : null; // Include entire end date
    
    if (startDate && itemDateObj < startDate) return false;
    if (endDate && itemDateObj > endDate) return false;
    return true;
  }, [dateRangeStart, dateRangeEnd]);

  // Precompute combined totals by account to avoid recomputing on expand/minimize
  const totalsByAccount = useMemo(() => {
    const map = {};
    for (const acc of studentAccounts) {
      const combined = createEmptyHours();
      const cards = acc.accesscards || [];
      for (const c of cards) {
        if (c.role !== 'Student') continue;
        const ens = c.enrollments || [];
        for (const e of ens) {
          if (selectedPeriod !== 'All Terms' && e.period_id !== selectedPeriod) continue;
          const stats = hoursByEnrollment[e.enrollment_id];
          if (!stats) continue;
          
          // If date range is set, recalculate from filtered items
          const hasDateFilter = dateRangeStart || dateRangeEnd;
          
          if (hasDateFilter) {
            // Recalculate totals from items filtered by date range
            if (stats.items) {
              stats.items.forEach(item => {
                // Apply date range filter
                if (!isItemInDateRange(item.date)) return;
                
                // Apply countPendingAsVerified filter for FE events
                if (item.source === 'Event' && item.type === 'Field Experience') {
                  if (!countPendingAsVerified && !item.title.includes('(Verified)')) {
                    return; // Skip pending items when not counting as verified
                  }
                }
                
                const t = item.type;
                if (t && combined[t] !== undefined) {
                  combined[t] += item.hours || 0;
                }
              });
            }
          } else {
            // Use precomputed totals when no date filter
            // Apply countPendingAsVerified logic to Field Experience hours
            if (countPendingAsVerified) {
              // When counting pending as verified, use all FE hours (they're all treated as verified)
              for (const t of HOUR_TYPES) combined[t] += stats.totals[t] || 0;
            } else {
              // When not counting pending as verified, only count verified FE events
              let verifiedFEHours = 0;
              if (stats.items) {
                stats.items.forEach(item => {
                  if (item.source === 'Event' && item.type === 'Field Experience' && item.title.includes('(Verified)')) {
                    verifiedFEHours += item.hours || 0;
                  }
                });
              }
              combined['Field Experience'] = verifiedFEHours;
              
              // Add other hour types normally
              for (const t of HOUR_TYPES) {
                if (t !== 'Field Experience') {
                  combined[t] += stats.totals[t] || 0;
                }
              }
            }
          }
        }
      }
      map[acc.account_id] = combined;
    }
    return map;
  }, [studentAccounts, hoursByEnrollment, selectedPeriod, countPendingAsVerified, dateRangeStart, dateRangeEnd, isItemInDateRange]);

  // Calculate pending hours for each student
  const pendingHoursByAccount = useMemo(() => {
    const map = {};
    for (const acc of studentAccounts) {
      let pendingHours = 0;
      const cards = acc.accesscards || [];
      for (const c of cards) {
        if (c.role !== 'Student') continue;
        const ens = c.enrollments || [];
        for (const e of ens) {
          if (selectedPeriod !== 'All Terms' && e.period_id !== selectedPeriod) continue;
          const stats = hoursByEnrollment[e.enrollment_id];
          if (!stats || !stats.items) continue;
          
          // Count pending FE hours (only when countPendingAsVerified is false)
          if (!countPendingAsVerified) {
            stats.items.forEach(item => {
              // Apply date range filter
              if (!isItemInDateRange(item.date)) return;
              
              if (item.source === 'Event' && item.type === 'Field Experience' && !item.title.includes('(Verified)')) {
                pendingHours += item.hours || 0;
              }
            });
          }
        }
      }
      map[acc.account_id] = pendingHours;
    }
    return map;
  }, [studentAccounts, hoursByEnrollment, selectedPeriod, countPendingAsVerified, isItemInDateRange]);

  const sortedStudents = useMemo(() => {
    // Parse tokens from debounced query for live filtering
    const tokens = debouncedQuery.split(/\r?\n/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const filtered = tokens.length === 0 ? studentAccounts : studentAccounts.filter(acc => {
      const name = `${acc.first_name || ''} ${acc.last_name || ''}`.toLowerCase();
      const email = (acc.primary_email || '').toLowerCase();
      return tokens.some(tok => name.includes(tok) || email.includes(tok));
    });

    const list = [...filtered];
    const getTotal = (acc) => {
      const t = totalsByAccount[acc.account_id] || createEmptyHours();
      return HOUR_TYPES.reduce((s, k) => s + (t[k] || 0), 0);
    };
    if (sortBy === 'name') {
      return list.sort((a, b) => {
        const an = `${a.first_name} ${a.last_name}`.toLowerCase();
        const bn = `${b.first_name} ${b.last_name}`.toLowerCase();
        return an.localeCompare(bn);
      });
    }
    if (sortBy === 'total') {
      return list.sort((a, b) => getTotal(b) - getTotal(a));
    }
    // Sort by specific hour type desc
    return list.sort((a, b) => {
      const ta = (totalsByAccount[a.account_id] || createEmptyHours())[sortBy] || 0;
      const tb = (totalsByAccount[b.account_id] || createEmptyHours())[sortBy] || 0;
      return tb - ta;
    });
  }, [studentAccounts, totalsByAccount, sortBy, debouncedQuery]);

  // Pagination constants
  const ITEMS_PER_PAGE = 15;
  
  // Calculate pagination
  const totalPages = Math.ceil(sortedStudents.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedStudents = sortedStudents.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPeriod, selectedAssociations, associationFilterOn, sortBy, debouncedQuery, countPendingAsVerified, includeWithdrawnStudents, dateRangeStart, dateRangeEnd]);

  // Calculate analytics
  const analytics = useMemo(() => {
    const totalStudents = sortedStudents.length;
    let studentsWithHours = 0;
    let studentsWithEvent = 0;
    let studentsWithCoursework = 0;
    let studentsWithProject = 0;
    let studentsWithWaiver = 0;
    let studentsWithMinTotalHours = 0;
    const studentsWithMinHoursByType = {
      'Field Experience': 0,
      'Lab': 0,
      'Concentration': 0,
      'Capstone': 0,
      'Theory': 0
    };

    sortedStudents.forEach(acc => {
      const cards = acc.accesscards || [];
      let hasAnyHours = false;
      let hasEvent = false;
      let hasCoursework = false;
      let hasProject = false;
      let hasWaiver = false;
      
      // Calculate totals for this student (across all enrollments)
      const studentTotals = createEmptyHours();
      let studentTotalHours = 0;

      cards.forEach(card => {
        if (card.role !== 'Student') return;
        const enrollments = card.enrollments || [];
        enrollments.forEach(enrollment => {
          // Check if enrollment matches selected period filter
          if (selectedPeriod !== 'All Terms' && enrollment.period_id !== selectedPeriod) return;
          
          // Check if enrollment matches selected association filter
          if (!enrollmentMatchesAssociation(enrollment)) return;
          
          const stats = hoursByEnrollment[enrollment.enrollment_id];
          if (!stats || !stats.items) return;

          // Calculate hours for this enrollment (respecting countPendingAsVerified and date range settings)
          let enrollmentTotalHours = 0;
          const enrollmentTotals = createEmptyHours();
          const hasDateFilter = dateRangeStart || dateRangeEnd;
          
          if (hasDateFilter) {
            // Recalculate from items when date filter is active
            stats.items.forEach(item => {
              // Apply date range filter
              if (!isItemInDateRange(item.date)) return;
              
              // Apply countPendingAsVerified filter for FE events
              if (item.source === 'Event' && item.type === 'Field Experience') {
                if (!countPendingAsVerified && !item.title.includes('(Verified)')) {
                  return; // Skip pending items when not counting as verified
                }
              }
              
              const t = item.type;
              if (t && enrollmentTotals[t] !== undefined) {
                enrollmentTotals[t] += item.hours || 0;
                enrollmentTotalHours += item.hours || 0;
              }
            });
          } else {
            // Use precomputed totals when no date filter
            if (countPendingAsVerified) {
              // When counting pending as verified, use all FE hours
              enrollmentTotalHours = HOUR_TYPES.reduce((sum, type) => sum + (stats.totals[type] || 0), 0);
              HOUR_TYPES.forEach(type => {
                enrollmentTotals[type] = stats.totals[type] || 0;
              });
            } else {
              // When not counting pending as verified, only count verified FE events
              let verifiedFEHours = 0;
              stats.items.forEach(item => {
                if (item.source === 'Event' && item.type === 'Field Experience' && item.title.includes('(Verified)')) {
                  verifiedFEHours += item.hours || 0;
                }
              });
              enrollmentTotals['Field Experience'] = verifiedFEHours;
              enrollmentTotalHours = verifiedFEHours;
              // Add other hour types
              HOUR_TYPES.forEach(type => {
                if (type !== 'Field Experience') {
                  enrollmentTotals[type] = stats.totals[type] || 0;
                  enrollmentTotalHours += stats.totals[type] || 0;
                }
              });
            }
          }
          
          // Accumulate totals for student
          studentTotalHours += enrollmentTotalHours;
          HOUR_TYPES.forEach(type => {
            studentTotals[type] += enrollmentTotals[type];
          });

          if (enrollmentTotalHours > 0) {
            hasAnyHours = true;
          }

          // Check for each form type
          stats.items.forEach(item => {
            // Apply date range filter
            if (!isItemInDateRange(item.date)) return;
            
            // Apply countPendingAsVerified filter for FE events
            if (item.source === 'Event' && item.type === 'Field Experience') {
              if (!countPendingAsVerified && !item.title.includes('(Verified)')) {
                return; // Skip pending items when not counting as verified
              }
            }

            if (item.source === 'Event') hasEvent = true;
            if (item.source === 'Coursework') hasCoursework = true;
            if (item.source === 'Project') hasProject = true;
            if (item.source === 'Waiver') hasWaiver = true;
          });
        });
      });

      if (hasAnyHours) studentsWithHours++;
      if (hasEvent) studentsWithEvent++;
      if (hasCoursework) studentsWithCoursework++;
      if (hasProject) studentsWithProject++;
      if (hasWaiver) studentsWithWaiver++;
      
      // Check threshold for total hours
      if (studentTotalHours >= minTotalHours) {
        studentsWithMinTotalHours++;
      }
      
      // Check thresholds for each hour type
      HOUR_TYPES.forEach(type => {
        if (studentTotals[type] >= minHoursByType[type]) {
          studentsWithMinHoursByType[type]++;
        }
      });
    });

    return {
      totalStudents,
      studentsWithHours,
      studentsWithEvent,
      studentsWithCoursework,
      studentsWithProject,
      studentsWithWaiver,
      studentsWithMinTotalHours,
      studentsWithMinHoursByType
    };
  }, [sortedStudents, hoursByEnrollment, selectedPeriod, selectedAssociations, associationFilterOn, countPendingAsVerified, minTotalHours, minHoursByType, dateRangeStart, dateRangeEnd, isItemInDateRange, enrollmentMatchesAssociation]);

  // Calculate sum of hours for students meeting threshold criteria
  const thresholdSumHours = useMemo(() => {
    const sumTotals = createEmptyHours();
    let sumTotalHours = 0;
    let studentsCounted = 0;

    sortedStudents.forEach(acc => {
      const cards = acc.accesscards || [];
      const studentTotals = createEmptyHours();
      let studentTotalHours = 0;

      cards.forEach(card => {
        if (card.role !== 'Student') return;
        const enrollments = card.enrollments || [];
        enrollments.forEach(enrollment => {
          if (selectedPeriod !== 'All Terms' && enrollment.period_id !== selectedPeriod) return;
          if (!enrollmentMatchesAssociation(enrollment)) return;
          
          const stats = hoursByEnrollment[enrollment.enrollment_id];
          if (!stats || !stats.items) return;

          let enrollmentTotalHours = 0;
          const enrollmentTotals = createEmptyHours();
          const hasDateFilter = dateRangeStart || dateRangeEnd;
          
          if (hasDateFilter) {
            stats.items.forEach(item => {
              if (!isItemInDateRange(item.date)) return;
              if (item.source === 'Event' && item.type === 'Field Experience') {
                if (!countPendingAsVerified && !item.title.includes('(Verified)')) return;
              }
              const t = item.type;
              if (t && enrollmentTotals[t] !== undefined) {
                enrollmentTotals[t] += item.hours || 0;
                enrollmentTotalHours += item.hours || 0;
              }
            });
          } else {
            if (countPendingAsVerified) {
              enrollmentTotalHours = HOUR_TYPES.reduce((sum, type) => sum + (stats.totals[type] || 0), 0);
              HOUR_TYPES.forEach(type => {
                enrollmentTotals[type] = stats.totals[type] || 0;
              });
            } else {
              let verifiedFEHours = 0;
              stats.items.forEach(item => {
                if (item.source === 'Event' && item.type === 'Field Experience' && item.title.includes('(Verified)')) {
                  verifiedFEHours += item.hours || 0;
                }
              });
              enrollmentTotals['Field Experience'] = verifiedFEHours;
              enrollmentTotalHours = verifiedFEHours;
              HOUR_TYPES.forEach(type => {
                if (type !== 'Field Experience') {
                  enrollmentTotals[type] = stats.totals[type] || 0;
                  enrollmentTotalHours += stats.totals[type] || 0;
                }
              });
            }
          }
          
          studentTotalHours += enrollmentTotalHours;
          HOUR_TYPES.forEach(type => {
            studentTotals[type] += enrollmentTotals[type];
          });
        });
      });

      // Check if student meets threshold criteria
      let meetsThreshold = false;
      if (minTotalHours > 0 && studentTotalHours >= minTotalHours) {
        meetsThreshold = true;
      }
      HOUR_TYPES.forEach(type => {
        if (minHoursByType[type] > 0 && studentTotals[type] >= minHoursByType[type]) {
          meetsThreshold = true;
        }
      });

      // If any threshold is set, only count students meeting at least one threshold
      // If no thresholds are set, count all students
      const hasAnyThreshold = minTotalHours > 0 || HOUR_TYPES.some(type => minHoursByType[type] > 0);
      
      if (!hasAnyThreshold || meetsThreshold) {
        studentsCounted++;
        sumTotalHours += studentTotalHours;
        HOUR_TYPES.forEach(type => {
          sumTotals[type] += studentTotals[type];
        });
      }
    });

    return {
      totals: sumTotals,
      totalHours: sumTotalHours,
      studentsCounted
    };
  }, [sortedStudents, hoursByEnrollment, selectedPeriod, selectedAssociations, associationFilterOn, countPendingAsVerified, dateRangeStart, dateRangeEnd, minTotalHours, minHoursByType, isItemInDateRange, enrollmentMatchesAssociation]);

  // CSV download functionality - Hour Totals
  const downloadCSV = useCallback(() => {
    const csvData = sortedStudents.map(acc => {
      const totals = totalsByAccount[acc.account_id] || createEmptyHours();
      const totalHours = HOUR_TYPES.reduce((sum, type) => sum + (totals[type] || 0), 0);
      
      // Get associations for this student (matching current filters)
      const associations = new Set();
      const cards = acc.accesscards || [];
      cards.forEach(card => {
        if (card.role !== 'Student') return;
        const enrollments = card.enrollments || [];
        enrollments.forEach(enrollment => {
          // Check if enrollment matches selected period filter
          if (selectedPeriod !== 'All Terms' && enrollment.period_id !== selectedPeriod) return;
          
          // Check if enrollment matches selected association filter
          if (!enrollmentMatchesAssociation(enrollment)) return;
          
          const associationName = enrollment.association_title || enrollment.association_id || 'N/A';
          if (associationName !== 'N/A') {
            associations.add(associationName);
          }
        });
      });
      const associationText = associations.size > 0 ? Array.from(associations).join('; ') : 'N/A';
      
      return {
        name: `${acc.first_name} ${acc.last_name}`,
        email: acc.primary_email || '',
        association: associationText,
        totalHours,
        fieldExperience: totals['Field Experience'] || 0,
        lab: totals['Lab'] || 0,
        concentration: totals['Concentration'] || 0,
        capstone: totals['Capstone'] || 0,
        theory: totals['Theory'] || 0
      };
    });

    // Convert to CSV format
    const headers = ['Name', 'Email', 'Association', 'Total Hours', 'Field Experience', 'Lab', 'Concentration', 'Capstone', 'Theory'];
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => [
        `"${row.name}"`,
        `"${row.email}"`,
        `"${row.association}"`,
        row.totalHours,
        row.fieldExperience,
        row.lab,
        row.concentration,
        row.capstone,
        row.theory
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const verifiedText = countPendingAsVerified ? 'pending-as-verified' : 'separate-statuses';
    link.setAttribute('download', `student-hours-totals-${verifiedText}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowDownloadPopup(false);
  }, [sortedStudents, totalsByAccount, countPendingAsVerified, selectedPeriod, selectedAssociations, associationFilterOn, enrollmentMatchesAssociation]);

  // CSV download functionality - All Items
  const downloadAllItemsCSV = useCallback(() => {
    const allItems = [];
    
    // Iterate through all sorted students
    sortedStudents.forEach(acc => {
      const cards = acc.accesscards || [];
      cards.forEach(card => {
        if (card.role !== 'Student') return;
        const enrollments = card.enrollments || [];
        enrollments.forEach(enrollment => {
          // Check if enrollment matches selected period filter
          if (selectedPeriod !== 'All Terms' && enrollment.period_id !== selectedPeriod) return;
          
          // Check if enrollment matches selected association filter
          if (!enrollmentMatchesAssociation(enrollment)) return;
          
          const stats = hoursByEnrollment[enrollment.enrollment_id];
          if (!stats || !stats.items) return;
          
          // Get association name
          const associationName = enrollment.association_title || enrollment.association_id || 'N/A';
          
          // Add each item
          stats.items.forEach(item => {
            // Apply date range filter
            if (!isItemInDateRange(item.date)) return;
            
            // Apply countPendingAsVerified filter for FE events
            if (item.source === 'Event' && item.type === 'Field Experience') {
              if (!countPendingAsVerified && !item.title.includes('(Verified)')) {
                return; // Skip pending items when not counting as verified
              }
            }
            
            // Format date-time
            const dateTime = item.date ? new Date(item.date).toLocaleString() : 'N/A';
            
            allItems.push({
              name: `${acc.first_name} ${acc.last_name}`,
              email: acc.primary_email || '',
              association: associationName,
              form: item.source, // Project, Event, Waiver, Coursework
              hourType: item.type || 'N/A', // Field Experience, Lab, Theory, Concentration, Capstone
              hours: item.hours || 0,
              when: dateTime
            });
          });
        });
      });
    });

    // Convert to CSV format
    const headers = ['Name', 'Email', 'Association', 'Form', 'Hour Type', 'Hours', 'When'];
    const csvContent = [
      headers.join(','),
      ...allItems.map(row => [
        `"${row.name}"`,
        `"${row.email}"`,
        `"${row.association}"`,
        `"${row.form}"`,
        `"${row.hourType}"`,
        row.hours,
        `"${row.when}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const verifiedText = countPendingAsVerified ? 'pending-as-verified' : 'separate-statuses';
    link.setAttribute('download', `student-hours-all-items-${verifiedText}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowDownloadPopup(false);
  }, [sortedStudents, hoursByEnrollment, selectedPeriod, selectedAssociations, associationFilterOn, countPendingAsVerified, isItemInDateRange, enrollmentMatchesAssociation]);

  // CSV download functionality - All Items (including students with no items)
  const downloadAllItemsIncludingNoItemsCSV = useCallback(() => {
    const allItems = [];
    
    // Get term name for "When" field if no date range
    const selectedPeriodObj = studentPeriods.find(p => p.id === selectedPeriod);
    const termName = selectedPeriodObj?.name || 'All Terms';
    const whenText = dateRangeStart || dateRangeEnd 
      ? `${dateRangeStart || 'Start'} to ${dateRangeEnd || 'End'}`
      : `All of ${termName}`;
    
    // Track which students have items (after applying all filters)
    const studentsWithItems = new Set();
    
    // First, collect all items (same as downloadAllItemsCSV)
    sortedStudents.forEach(acc => {
      const cards = acc.accesscards || [];
      let studentHasItems = false;
      
      cards.forEach(card => {
        if (card.role !== 'Student') return;
        const enrollments = card.enrollments || [];
        enrollments.forEach(enrollment => {
          // Check if enrollment matches selected period filter
          if (selectedPeriod !== 'All Terms' && enrollment.period_id !== selectedPeriod) return;
          
          // Check if enrollment matches selected association filter
          if (!enrollmentMatchesAssociation(enrollment)) return;
          
          const stats = hoursByEnrollment[enrollment.enrollment_id];
          if (!stats || !stats.items) return;
          
          // Get association name
          const associationName = enrollment.association_title || enrollment.association_id || 'N/A';
          
          // Add each item
          stats.items.forEach(item => {
            // Apply date range filter
            if (!isItemInDateRange(item.date)) return;
            
            // Apply countPendingAsVerified filter for FE events
            if (item.source === 'Event' && item.type === 'Field Experience') {
              if (!countPendingAsVerified && !item.title.includes('(Verified)')) {
                return; // Skip pending items when not counting as verified
              }
            }
            
            // If we get here, the item passed all filters - mark student as having items
            studentHasItems = true;
            
            // Format date-time
            const dateTime = item.date ? new Date(item.date).toLocaleString() : 'N/A';
            
            allItems.push({
              name: `${acc.first_name} ${acc.last_name}`,
              email: acc.primary_email || '',
              association: associationName,
              form: item.source, // Project, Event, Waiver, Coursework
              hourType: item.type || 'N/A', // Field Experience, Lab, Theory, Concentration, Capstone
              hours: item.hours || 0,
              when: dateTime
            });
          });
        });
      });
      
      // Track that this student has items after all filters
      if (studentHasItems) {
        studentsWithItems.add(acc.account_id);
      }
    });
    
    // Now add students with no items
    sortedStudents.forEach(acc => {
      if (!studentsWithItems.has(acc.account_id)) {
        // Get association name for this student
        const cards = acc.accesscards || [];
        let associationName = 'N/A';
        
        cards.forEach(card => {
          if (card.role !== 'Student') return;
          const enrollments = card.enrollments || [];
          enrollments.forEach(enrollment => {
            // Check if enrollment matches selected period filter
            if (selectedPeriod !== 'All Terms' && enrollment.period_id !== selectedPeriod) return;
            
            // Check if enrollment matches selected association filter
            if (!enrollmentMatchesAssociation(enrollment)) return;
            
            const assoc = enrollment.association_title || enrollment.association_id;
            if (assoc && assoc !== 'N/A') {
              associationName = assoc;
            }
          });
        });
        
        allItems.push({
          name: `${acc.first_name} ${acc.last_name}`,
          email: acc.primary_email || '',
          association: associationName,
          form: 'No Items',
          hourType: 'N/A',
          hours: 'N/A',
          when: whenText
        });
      }
    });

    // Convert to CSV format
    const headers = ['Name', 'Email', 'Association', 'Form', 'Hour Type', 'Hours', 'When'];
    const csvContent = [
      headers.join(','),
      ...allItems.map(row => [
        `"${row.name}"`,
        `"${row.email}"`,
        `"${row.association}"`,
        `"${row.form}"`,
        `"${row.hourType}"`,
        typeof row.hours === 'number' ? row.hours : `"${row.hours}"`,
        `"${row.when}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const verifiedText = countPendingAsVerified ? 'pending-as-verified' : 'separate-statuses';
    link.setAttribute('download', `student-hours-all-items-including-empty-${verifiedText}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowDownloadPopup(false);
  }, [sortedStudents, hoursByEnrollment, selectedPeriod, selectedAssociations, associationFilterOn, countPendingAsVerified, isItemInDateRange, studentPeriods, dateRangeStart, dateRangeEnd, enrollmentMatchesAssociation]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'red', padding: '2rem', textAlign: 'center' }}>{error}</div>
    );
  }

  return (
    <div style={{ padding: '20px', paddingBottom: totalPages > 1 ? '120px' : '20px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Add CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      {showTorLoadPrompt && (
        <>
          <div
            role="presentation"
            onClick={declineTorAssignedStudents}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              backgroundColor: 'rgba(0,0,0,0.35)'
            }}
          />
          <div
            role="dialog"
            aria-labelledby="tor-load-prompt-title"
            aria-describedby="tor-load-prompt-desc"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e5e7eb',
              zIndex: 1001,
              width: 'min(440px, 94vw)',
              overflow: 'hidden'
            }}
          >
            <div style={{ padding: '22px 24px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 id="tor-load-prompt-title" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                Teacher of record
              </h2>
              <p id="tor-load-prompt-desc" style={{ margin: '12px 0 0', fontSize: '15px', color: '#4b5563', lineHeight: 1.5 }}>
                As teacher of record, you have been assigned certain students. Would you like to load those students now?
              </p>
              <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#6b7280' }}>
                Yes applies your assigned student emails and turns association filtering off for this view. No keeps the usual filters.
              </p>
            </div>
            <div style={{ padding: '16px 24px 22px', display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={declineTorAssignedStudents}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={acceptTorAssignedStudents}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </>
      )}
      
      <h1 style={{ 
        margin: 0, 
        marginBottom: '24px', 
        color: '#111827',
        fontSize: '32px',
        fontWeight: 700,
        letterSpacing: '-0.025em'
      }}>
        Student Hours Overview
      </h1>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        flexWrap: 'wrap', 
        marginBottom: '32px', 
        alignItems: 'center',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '1px solid #e2e8f0'
      }}>
        {/* School filter */}
        <select
          value={selectedSchool ? selectedSchool.school_id : ''}
          onChange={(e) => {
            const sid = e.target.value;
            if (!sid) setSelectedSchool(null);
            else {
              const s = schools.find(sc => sc.school_id === sid);
              if (s) setSelectedSchool(s);
            }
          }}
          disabled
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6', color: '#6b7280' }}
        >
          <option value="">All Schools</option>
          {schools.map(s => (
            <option key={s.school_id} value={s.school_id}>{s.school_name}</option>
          ))}
        </select>

        {/* Term filter */}
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white' }}
        >
          <option value="All Terms">All Terms</option>
          {studentPeriods.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        {/* Association filter - By Association with On/Off toggle, checkboxes when ON */}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              minWidth: '180px'
            }}
          >
            <button
              type="button"
              onClick={() => setShowAssociationPopup(prev => !prev)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
                fontWeight: 500
              }}
            >
              By Association
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: associationFilterOn ? '#22c55e' : '#9ca3af',
                  flexShrink: 0
                }}
                title={associationFilterOn ? 'Filter is ON' : 'Filter is OFF'}
              />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setAssociationFilterOn(prev => !prev);
              }}
              style={{
                padding: '4px 10px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: associationFilterOn ? '#f0fdf4' : '#f3f4f6',
                color: associationFilterOn ? '#166534' : '#6b7280',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              {associationFilterOn ? 'On' : 'Off'}
            </button>
          </div>
          {showAssociationPopup && (
            <>
              <div
                onClick={() => setShowAssociationPopup(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 998
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #e5e7eb',
                  zIndex: 999,
                  minWidth: '240px',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  padding: '8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Filter by association</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>On</span>
                    <input
                      type="checkbox"
                      checked={associationFilterOn}
                      onChange={(e) => {
                        setAssociationFilterOn(e.target.checked);
                      }}
                      style={{ margin: 0, cursor: 'pointer', accentColor: '#22c55e' }}
                    />
                  </label>
                </div>
                {associationFilterOn ? (
                  <>
                    <div style={{ display: 'flex', gap: '8px', padding: '8px 12px', marginBottom: '4px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const allIds = [...studentAssociations.map(a => a.id), UNKNOWN_ASSOCIATION_ID];
                          setSelectedAssociations(new Set(allIds));
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: '#f9fafb',
                          cursor: 'pointer',
                          color: '#374151'
                        }}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAssociations(new Set())}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: '#f9fafb',
                          cursor: 'pointer',
                          color: '#374151'
                        }}
                      >
                        Deselect All
                      </button>
                    </div>
                    {studentAssociations.map(association => (
                      <label
                        key={association.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderRadius: '6px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAssociations.has(association.id)}
                          onChange={(e) => {
                            setSelectedAssociations(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                next.add(association.id);
                              } else {
                                next.delete(association.id);
                              }
                              return next;
                            });
                          }}
                          style={{ margin: 0, cursor: 'pointer', accentColor: '#3b82f6' }}
                        />
                        <span style={{ fontSize: '14px', color: '#374151' }}>{association.title}</span>
                      </label>
                    ))}
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        borderTop: '1px solid #e5e7eb',
                        marginTop: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAssociations.has(UNKNOWN_ASSOCIATION_ID)}
                        onChange={(e) => {
                          setSelectedAssociations(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) {
                              next.add(UNKNOWN_ASSOCIATION_ID);
                            } else {
                              next.delete(UNKNOWN_ASSOCIATION_ID);
                            }
                            return next;
                          });
                        }}
                        style={{ margin: 0, cursor: 'pointer', accentColor: '#3b82f6' }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>Unknown association</span>
                    </label>
                  </>
                ) : (
                  <div style={{ fontSize: '13px', color: '#6b7280', padding: '12px', fontStyle: 'italic' }}>
                    Filter is off — showing all students regardless of association.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sort control */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }}
        >
          <option value="name">Sort: Name (A→Z)</option>
          <option value="total">Sort: Total Hours (desc)</option>
          <option value="Field Experience">Sort: Field Experience (desc)</option>
          <option value="Lab">Sort: Lab (desc)</option>
          <option value="Theory">Sort: Theory (desc)</option>
          <option value="Capstone">Sort: Capstone (desc)</option>
          <option value="Concentration">Sort: Concentration (desc)</option>
        </select>

        {/* Bulk filter by name/email (one per line) */}
        <textarea
          value={bulkQuery}
          onChange={(e) => setBulkQuery(e.target.value)}
          placeholder={"Type names or emails to filter live (one per line)"}
          rows={3}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', minWidth: 280 }}
        />
        
        {/* Count Pending as Verified Toggle */}
        <label style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          fontSize: '14px', 
          color: '#374151',
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: '8px',
          backgroundColor: countPendingAsVerified ? '#f0f9ff' : 'transparent',
          border: countPendingAsVerified ? '1px solid #3b82f6' : '1px solid transparent',
          transition: 'all 0.2s ease'
        }}>
          <div style={{ position: 'relative' }}>
            <input
              type="checkbox"
              checked={countPendingAsVerified}
              onChange={(e) => setCountPendingAsVerified(e.target.checked)}
              style={{ 
                margin: 0,
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#3b82f6'
              }}
            />
            {countPendingAsVerified && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '12px',
                color: '#3b82f6',
                fontWeight: 'bold',
                pointerEvents: 'none',
                marginTop: '-1px'
              }}>
                ✓
              </div>
            )}
          </div>
          <span style={{ fontWeight: countPendingAsVerified ? 600 : 400 }}>
            Count Pending FE Event Hours as Verified
          </span>
        </label>

        {/* Include Withdrawn Students Toggle */}
        <label style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          fontSize: '14px', 
          color: '#374151',
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: '8px',
          backgroundColor: includeWithdrawnStudents ? '#f0f9ff' : 'transparent',
          border: includeWithdrawnStudents ? '1px solid #3b82f6' : '1px solid transparent',
          transition: 'all 0.2s ease'
        }}>
          <div style={{ position: 'relative' }}>
            <input
              type="checkbox"
              checked={includeWithdrawnStudents}
              onChange={(e) => setIncludeWithdrawnStudents(e.target.checked)}
              style={{ 
                margin: 0,
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#3b82f6'
              }}
            />
            {includeWithdrawnStudents && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '12px',
                color: '#3b82f6',
                fontWeight: 'bold',
                pointerEvents: 'none',
                marginTop: '-1px'
              }}>
                ✓
              </div>
            )}
          </div>
          <span style={{ fontWeight: includeWithdrawnStudents ? 600 : 400 }}>
            Include Withdrawn Students
          </span>
        </label>

        {/* CSV Download */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowDownloadPopup(!showDownloadPopup)}
            title="Download CSV"
            style={{ 
              padding: '8px 16px', 
              borderRadius: 8, 
              border: '1px solid #d1d5db', 
              backgroundColor: 'white', 
              color: '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            Download
          </button>

          {/* Download Popup - larger modal with descriptions */}
          {showDownloadPopup && (
            <>
              {/* Backdrop */}
              <div
                onClick={() => setShowDownloadPopup(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 998,
                  backgroundColor: 'rgba(0,0,0,0.3)'
                }}
              />
              {/* Modal */}
              <div
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  border: '1px solid #e5e7eb',
                  zIndex: 999,
                  width: 'min(480px, 95vw)',
                  maxHeight: '85vh',
                  overflow: 'auto'
                }}
              >
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    Download CSV
                  </h3>
                  <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                    Choose an export format. All exports respect your current filters (term, associations, date range).
                  </p>
                </div>
                <div style={{ padding: '16px 24px 24px' }}>
                  <button
                    onClick={downloadCSV}
                    style={{
                      width: '100%',
                      padding: '16px',
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>Hour Totals</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>
                      One row per student with aggregated hours. Columns: Name, Email, Association, Total Hours, Field Experience, Lab, Concentration, Capstone, Theory. Best for summaries and reporting.
                    </div>
                  </button>
                  <button
                    onClick={downloadAllItemsCSV}
                    style={{
                      width: '100%',
                      padding: '16px',
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>All Items</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>
                      One row per hour item (each Event, Project, Waiver, Coursework). Columns: Name, Email, Association, Form, Hour Type, Hours, When. Only includes students who have at least one item. Best for detailed audit.
                    </div>
                  </button>
                  <button
                    onClick={downloadAllItemsIncludingNoItemsCSV}
                    style={{
                      width: '100%',
                      padding: '16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>All Items (including students with no items)</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>
                      Same as All Items but adds one row per student who has no items (Form: No Items, Hours: N/A). Ensures every student in the filtered list appears. Best for compliance or attendance tracking.
                    </div>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Date Range Filter */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          padding: '8px 12px',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          backgroundColor: 'white'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <label style={{
              fontSize: '14px',
              color: '#374151',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}>
              Date Range:
            </label>
            <input
              type="date"
              value={dateRangeStart}
              onChange={(e) => setDateRangeStart(e.target.value)}
              placeholder="Start Date"
              style={{
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '140px'
              }}
            />
            <span style={{ color: '#6b7280', fontSize: '14px' }}>to</span>
            <input
              type="date"
              value={dateRangeEnd}
              onChange={(e) => setDateRangeEnd(e.target.value)}
              placeholder="End Date"
              style={{
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '140px'
              }}
            />
            {(dateRangeStart || dateRangeEnd) && (
              <button
                onClick={() => {
                  setDateRangeStart('');
                  setDateRangeEnd('');
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            fontStyle: 'italic',
            marginLeft: '0',
            lineHeight: '1.4'
          }}>
            This will add a filter to include only students whose submissions or Event/Waiver date are within the range (inclusive)
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div style={{
        marginBottom: '32px',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '1px solid #e2e8f0'
      }}>
        <div
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            marginBottom: analyticsExpanded ? '16px' : '0'
          }}
        >
          <h2 style={{
            margin: 0,
            color: '#111827',
            fontSize: '18px',
            fontWeight: 600
          }}>
            Analytics
          </h2>
          <span style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s ease',
            transform: analyticsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
        {analyticsExpanded && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '12px'
          }}>
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              Results with Hours in at least 1 category
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827'
            }}>
              {analytics.studentsWithHours} out of {analytics.totalStudents}
            </div>
          </div>
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              Results with Hours from Event
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827'
            }}>
              {analytics.studentsWithEvent} out of {analytics.totalStudents}
            </div>
          </div>
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              Results with Hours from Coursework
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827'
            }}>
              {analytics.studentsWithCoursework} out of {analytics.totalStudents}
            </div>
          </div>
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              Results with Hours from Project
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827'
            }}>
              {analytics.studentsWithProject} out of {analytics.totalStudents}
            </div>
          </div>
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              Results with Hours from Waiver
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827'
            }}>
              {analytics.studentsWithWaiver} out of {analytics.totalStudents}
            </div>
          </div>
          </div>
        )}

        {/* Threshold-based Analytics */}
        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <div
            onClick={() => setThresholdAnalyticsExpanded(!thresholdAnalyticsExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: thresholdAnalyticsExpanded ? '16px' : '0'
            }}
          >
            <h3 style={{
              margin: 0,
              color: '#111827',
              fontSize: '16px',
              fontWeight: 600
            }}>
              Threshold Analytics
            </h3>
            <span style={{
              fontSize: '20px',
              color: '#6b7280',
              transition: 'transform 0.2s ease',
              transform: thresholdAnalyticsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </span>
          </div>
          {thresholdAnalyticsExpanded && (
            <React.Fragment>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '12px'
            }}>
            {/* Total Hours Threshold */}
            <div style={{
              padding: '12px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '8px'
              }}>
                Results with at least X total hours
              </div>
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <input
                  type="number"
                  min="0"
                  value={minTotalHours}
                  onChange={(e) => setMinTotalHours(Number(e.target.value) || 0)}
                  style={{
                    width: '80px',
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>hours</span>
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#111827'
              }}>
                {analytics.studentsWithMinTotalHours} out of {analytics.totalStudents}
              </div>
            </div>

            {/* Hour Type Thresholds */}
            {HOUR_TYPES.map(type => (
              <div key={type} style={{
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '8px'
                }}>
                  Results with at least X {type} hours
                </div>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <input
                    type="number"
                    min="0"
                    value={minHoursByType[type]}
                    onChange={(e) => setMinHoursByType(prev => ({
                      ...prev,
                      [type]: Number(e.target.value) || 0
                    }))}
                    style={{
                      width: '80px',
                      padding: '6px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>hours</span>
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#111827'
                }}>
                  {analytics.studentsWithMinHoursByType[type]} out of {analytics.totalStudents}
                </div>
              </div>
            ))}
            </div>
            </React.Fragment>
          )}
        </div>
      </div>

      {/* Sum of Hours Section */}
      <div style={{
        marginBottom: '32px',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{
          margin: 0,
          marginBottom: '16px',
          color: '#111827',
          fontSize: '16px',
          fontWeight: 600
        }}>
          Sum of Hours
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            padding: '8px',
            backgroundColor: 'white',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              Total Hours
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#111827'
            }}>
              {thresholdSumHours.totalHours.toFixed(1)}h
            </div>
          </div>
          {HOUR_TYPES.map(type => (
            <div key={type} style={{
              padding: '8px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                {type}
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#111827'
              }}>
                {thresholdSumHours.totals[type].toFixed(1)}h
              </div>
            </div>
          ))}
        </div>
        <div style={{
          fontSize: '12px',
          color: '#6b7280',
          fontStyle: 'italic'
        }}>
          Based on {thresholdSumHours.studentsCounted} student{thresholdSumHours.studentsCounted !== 1 ? 's' : ''} meeting threshold criteria
        </div>
      </div>

        {/* Student list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {paginatedStudents.map(acc => {
            // Use precomputed totals for the account
            const combined = totalsByAccount[acc.account_id] || createEmptyHours();
            const isSelected = selectedStudent && selectedStudent.account_id === acc.account_id;
            const totalHours = HOUR_TYPES.reduce((s, k) => s + (combined[k] || 0), 0);

            return (
              <div 
                key={acc.account_id} 
                onClick={() => selectStudent(acc)}
                style={{ 
                  border: `2px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`, 
                  borderRadius: 16, 
                  backgroundColor: isSelected ? '#f0f9ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isSelected 
                    ? '0 10px 25px -5px rgba(59, 130, 246, 0.1), 0 4px 6px -2px rgba(59, 130, 246, 0.05)'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                  ':hover': {
                    borderColor: '#3b82f6',
                    backgroundColor: '#f0f9ff',
                    boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.1), 0 4px 6px -2px rgba(59, 130, 246, 0.05)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.backgroundColor = '#f0f9ff';
                    e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(59, 130, 246, 0.1), 0 4px 6px -2px rgba(59, 130, 246, 0.05)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <div style={{
                  padding: '20px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: '#111827', 
                      fontSize: '18px',
                      marginBottom: '4px',
                      letterSpacing: '-0.025em'
                    }}>
                      {acc.first_name} {acc.last_name}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#6b7280',
                      fontWeight: 400
                    }}>
                      {acc.primary_email}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {/* Total hours badge with pending disclaimer */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ 
                        fontSize: '16px', 
                        color: 'white', 
                        fontWeight: 700,
                        backgroundColor: totalHours > 0 ? '#10b981' : '#6b7280',
                        padding: '8px 16px',
                        borderRadius: '24px',
                        minWidth: '80px',
                        textAlign: 'center',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease'
                      }}>
                        {totalHours}h
                      </div>
                      {/* Pending hours disclaimer */}
                      {!countPendingAsVerified && pendingHoursByAccount[acc.account_id] > 0 && (
                        <div style={{
                          fontSize: '11px',
                          color: '#dc2626',
                          fontWeight: 500,
                          textAlign: 'center',
                          opacity: 0.8
                        }}>
                          {pendingHoursByAccount[acc.account_id]}h pending
                        </div>
                      )}
                    </div>
                    
                    {/* Hour type chips */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {HOUR_TYPES.map(t => {
                        const hours = combined[t] || 0;
                        return (
                          <div 
                            key={`${acc.account_id}-${t}`} 
                            style={{ 
                              fontSize: '12px', 
                              color: hours > 0 ? '#111827' : '#9ca3af',
                              backgroundColor: hours > 0 ? '#f3f4f6' : '#f9fafb',
                              padding: '6px 12px',
                              borderRadius: '12px',
                              fontWeight: 500,
                              minWidth: '70px',
                              textAlign: 'center',
                              border: hours > 0 ? '1px solid #e5e7eb' : '1px solid #f3f4f6',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ fontWeight: 700, fontSize: '14px', color: hours > 0 ? '#111827' : '#9ca3af' }}>
                              {hours}h
                            </div>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px', lineHeight: 1 }}>
                              {t}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Selection indicator */}
                    {isSelected && (
                      <div style={{ 
                        color: '#3b82f6', 
                        fontSize: '20px', 
                        marginLeft: '8px',
                        animation: 'pulse 2s infinite'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination Controls - Sticky at bottom */}
        {totalPages > 1 && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            padding: '20px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 100
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: 500,
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
            >
              Previous
            </button>

            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              {(() => {
                const pages = [];
                const maxVisible = 7;
                
                if (totalPages <= maxVisible) {
                  // Show all pages if total is small
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  // Always show first page
                  pages.push(1);
                  
                  let startPage = Math.max(2, currentPage - 1);
                  let endPage = Math.min(totalPages - 1, currentPage + 1);
                  
                  // Adjust if we're near the start
                  if (currentPage <= 3) {
                    endPage = Math.min(totalPages - 1, 4);
                  }
                  
                  // Adjust if we're near the end
                  if (currentPage >= totalPages - 2) {
                    startPage = Math.max(2, totalPages - 3);
                  }
                  
                  // Add ellipsis before middle pages if needed
                  if (startPage > 2) {
                    pages.push('ellipsis-start');
                  }
                  
                  // Add middle pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }
                  
                  // Add ellipsis after middle pages if needed
                  if (endPage < totalPages - 1) {
                    pages.push('ellipsis-end');
                  }
                  
                  // Always show last page
                  pages.push(totalPages);
                }
                
                return pages.map((page, idx) => {
                  if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                    return (
                      <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', padding: '0 4px' }}>
                        ...
                      </span>
                    );
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${currentPage === page ? '#3b82f6' : '#d1d5db'}`,
                        backgroundColor: currentPage === page ? '#3b82f6' : 'white',
                        color: currentPage === page ? 'white' : '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '14px',
                        fontWeight: currentPage === page ? 600 : 500,
                        minWidth: '40px'
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== page) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#9ca3af';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== page) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }
                      }}
                    >
                      {page}
                    </button>
                  );
                });
              })()}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: 500,
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
            >
              Next
            </button>

            <div style={{
              marginLeft: '16px',
              fontSize: '14px',
              color: '#6b7280',
              fontWeight: 500
            }}>
              Showing {startIndex + 1}-{Math.min(endIndex, sortedStudents.length)} of {sortedStudents.length}
            </div>
          </div>
        )}

        {/* Student Details Panel */}
        {selectedStudent && (
          <StudentDetailsPanel
            isOpen={!!selectedStudent}
            onClose={closeStudentPanel}
            student={selectedStudent}
            position={panelPosition}
            onPositionChange={setPanelPosition}
            enrollments={(() => {
              // Collect enrollments matching selectedPeriod and selectedAssociations for the selected student
              const studentCards = (selectedStudent.accesscards || []).filter(c => c.role === 'Student');
              const enrollments = [];
              studentCards.forEach(c => {
                (c.enrollments || []).forEach(e => {
                  if (selectedPeriod !== 'All Terms' && e.period_id !== selectedPeriod) return;
                  if (!enrollmentMatchesAssociation(e)) return;
                  enrollments.push(e);
                });
              });
              return enrollments;
            })()}
            hoursByEnrollment={hoursByEnrollment}
            loadingEnrollments={loadingEnrollments}
          />
        )}
    </div>
  );
}

export default StudentHoursOverview;


